import React from 'react';
import type { DataFreshness } from './types';

interface Props {
  fromDate: string;
  toDate: string;
  view: 'month' | 'week';
  onViewChange: (v: 'month' | 'week') => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onOpenPicker?: () => void;
  freshness: DataFreshness | null;
  onPrefetchPrev?: () => void;
  onPrefetchNext?: () => void;
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function parseIsoUtc(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/**
 * Period label between prev/next arrows.
 * Month view: "May 2026". Week view: "May 12 - May 18, 2026" or
 * "Apr 28 - May 4, 2026" when the week crosses a month boundary.
 * Caller passes the exclusive `toDate` (one day past Saturday) — we subtract
 * one day so the displayed end matches the rendered Saturday.
 */
function rangeLabel(view: 'month' | 'week', fromDate: string, toDate: string): string {
  if (view === 'month') {
    const d = parseIsoUtc(fromDate);
    return `${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  }
  const start = parseIsoUtc(fromDate);
  const endExclusive = parseIsoUtc(toDate);
  const end = new Date(endExclusive);
  end.setUTCDate(end.getUTCDate() - 1);
  const sameMonth = start.getUTCMonth() === end.getUTCMonth()
    && start.getUTCFullYear() === end.getUTCFullYear();
  const startLabel = `${MONTHS_SHORT[start.getUTCMonth()]} ${start.getUTCDate()}`;
  const endLabel = sameMonth
    ? `${MONTHS_SHORT[end.getUTCMonth()]} ${end.getUTCDate()}`
    : `${MONTHS_SHORT[end.getUTCMonth()]} ${end.getUTCDate()}`;
  return `${startLabel} - ${endLabel}, ${end.getUTCFullYear()}`;
}

export function CalendarToolbar({
  fromDate, toDate, view, onViewChange,
  onPrev, onNext, onToday, onOpenPicker, onPrefetchPrev, onPrefetchNext,
}: Props) {
  const periodLabel = rangeLabel(view, fromDate, toDate);

  return (
    <header className="grid grid-cols-3 items-center gap-3 px-4 py-3 border-b border-[var(--card-border)]" data-tour="calendar-toolbar">
      <div className="flex items-center gap-2 justify-self-start">
        {onOpenPicker && (
          <button
            type="button"
            onClick={onOpenPicker}
            className="text-sm cgm-text-secondary border border-[var(--card-border)] px-2 py-1"
            data-tour="calendar-switch-athlete"
          >
            Switch Athlete (⌘K)
          </button>
        )}
      </div>

      {/* Period nav is its own tour target — spotlighting the full 3-col header
          put the ring over empty left/right slots and looked "misplaced". */}
      <div className="flex items-center gap-2 justify-self-center" data-tour="calendar-period-nav">
        <button type="button" onClick={onPrev} onMouseEnter={onPrefetchPrev} onFocus={onPrefetchPrev} aria-label="Previous">‹</button>
        <button
          type="button"
          onClick={onToday}
          data-testid="calendar-period-label"
          title="Jump to today"
          className="text-sm font-medium cgm-text-primary min-w-[10rem] text-center"
        >
          {periodLabel}
        </button>
        <button type="button" onClick={onNext} onMouseEnter={onPrefetchNext} onFocus={onPrefetchNext} aria-label="Next">›</button>
        <div className="border-l border-[var(--card-border)] h-6 mx-2" />
        <button
          type="button"
          aria-pressed={view === 'month'}
          onClick={() => onViewChange('month')}
          className={view === 'month' ? 'font-medium cgm-text-primary' : 'cgm-text-muted'}
          data-tour="calendar-view-month"
        >
          Month
        </button>
        <button
          type="button"
          aria-pressed={view === 'week'}
          onClick={() => onViewChange('week')}
          className={view === 'week' ? 'font-medium cgm-text-primary' : 'cgm-text-muted'}
          data-tour="calendar-view-week"
        >
          Week
        </button>
      </div>

      <div className="justify-self-end" data-testid="toolbar-right-slot" />
    </header>
  );
}
